import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { AppModeService } from 'src/app/angular-wallet-base/services/app-mode.service';
import { Logger } from 'src/app/angular-wallet-base/services/logger.service';
import { AppState } from 'src/app/angular-wallet-base/store/appState';
import { ethers } from 'ethers';
import { MoralisService } from 'src/app/angular-wallet-base/services/moralis.service';
import { inspect } from 'util';
import { getSideMenuStatus } from 'src/app/angular-wallet-base/store/appSettings';
import { take } from 'rxjs/operators';
import { ImportOrHideAssetModalComponent } from '../import-or-hide-token-modal/import-or-hide-token.modal';
import { AuthService } from 'src/app/angular-wallet-base/services/auth.service';
import { SmallToCryptoPipe } from 'src/app/angular-wallet-base/pipes/smalltocrypto/smalltocrypto.pipe';
import { ERC20tokenData } from 'src/app/global';
import { getERC20TokenList, getKeychain } from 'src/app/angular-wallet-base/store/wallet';
import { CoinGeckoService } from 'src/app/angular-wallet-base/services/coingecko.service';

interface TokenDetailsFromMoralis {
    symbol: string;
    assetGuid: string;
    precision: string;
    logo: string;
    balance: string;
    chainId: string;
    name: string;
}

@Component({
    selector: 'app-new-token-modal',
    templateUrl: './add-new-token.modal.html',
    styleUrls: ['./add-new-token.modal.scss']
})
export class ImportAssetModalComponent implements OnInit {

    public isDesktopMode;
    public isSuccess: boolean = false;
    public successFailText: string;
    public title: string;
    public addAssetForm: FormGroup;
    public tokenDetailsFromMoralis: TokenDetailsFromMoralis;
    public tokensForImportERC20token: Array<any>;
    public erc20TokenDetails: ERC20tokenData;
    public tokenMetadataFromMoralis: any;
    constructor(
        private modalController: ModalController,
        public appMode: AppModeService,
        protected store: Store<AppState>,
        private formBuilder: FormBuilder,
        private moralisService: MoralisService,
        private authService: AuthService,
        private smallToCrypto: SmallToCryptoPipe,
        private coingeckoService: CoinGeckoService
    ) {
        this.appMode.isDesktopMode.subscribe(mode => this.isDesktopMode = mode);
    }

    async ngOnInit() {
        this.buildForm();
        this.erc20TokenDetails = this.moralisService.addERC20tokenData;
        Logger.info('moralisServiceERC20Token', this.erc20TokenDetails);
        Logger.info('tokensForImportERC20token', this.tokensForImportERC20token);
        // if we are getting back from the importOrHideAsset modal
        if (this.erc20TokenDetails) {
            await this.authService.dismissModal('importOrHideAssetModal');
            this.addAssetForm.patchValue({
                assetGuid: this.erc20TokenDetails.assetGuid,
                baseChainSymbol: this.erc20TokenDetails.baseChainSymbol,
                precision: this.erc20TokenDetails.precision,
                symbol: this.erc20TokenDetails.symbol,
                walletAddressOfSelectedNetwork: this.erc20TokenDetails.walletAddressOfSelectedNetwork,
                logo: this.erc20TokenDetails.logo,
                balance: this.erc20TokenDetails.balance,
                coingeckoId: this.erc20TokenDetails.coingeckoId,
                chainId: this.erc20TokenDetails.chainId,
                name: this.erc20TokenDetails.name,
            });
            this.tokensForImportERC20token = this.moralisService.tokensForImportERC20token;
            Logger.info('tokensForImportERC20token', this.tokensForImportERC20token);
        }
    }

    private async buildForm() {
        this.addAssetForm = this.formBuilder.group({
            assetGuid: [null, [Validators.required]],
            baseChainSymbol: ['AVAX', [Validators.required]],
            precision: [null, [Validators.required]],
            symbol: [null, [Validators.required]],
            logo: [null, [Validators.required]],
            balance: [null, [Validators.required]],
            walletAddressOfSelectedNetwork: [null],
            coingeckoId: [null],
            chainId: [null],
            name: [null]
        });
    }
    get f() { return this.addAssetForm.controls; }

    async onNetworkChange(ob) {
        this.addAssetForm.patchValue({
            precision: null,
            symbol: null,
            logo: null,
            balance: null,
            coingeckoId: null,
            chainId: null,
            name: null,
            walletAddressOfSelectedNetwork: null,
        });
        this.addAssetForm.controls.assetGuid.updateValueAndValidity();
        await this.validateAddressAndFetchMetadataOfToken();
    }
    closeModal() {
        this.moralisService.setERC20tokenData(null, null);
        this.modalController.dismiss();
    }

    async validateAddressAndFetchMetadataOfToken() {
        try {
            const address = this.addAssetForm.controls.assetGuid.value;
            const checkStatus = await this.checkDuplicateEntry(address);
            if (this.validateAddress(address) && checkStatus) {
                const response = await this.fetchTokenMetadata(address);
                if (response) {
                    this.setTokenDetails(response[0]);
                }
            }
        } catch (e) {
            Logger.error('validateAddressAndFetchMetadataOfToken', inspect(e));
        }
    }

    private async checkDuplicateEntry(address: string) {
        // check whether the address is already added
        const keychain = await this.store.select(getKeychain).pipe(take(1)).toPromise();
        const tokenList = Object.values(keychain);
        const hiddenAssets = await this.store.select(getERC20TokenList).pipe(take(1)).toPromise();
        Logger.info('hiddenAssets ', hiddenAssets);
        if (tokenList.find(token => token.guid?.toLowerCase() === address?.toLowerCase())) {
            this.addAssetForm.controls[`assetGuid`].setErrors({ duplicate: true });
            this.addAssetForm.controls[`assetGuid`].markAsDirty();
            return false;
        }
        if (hiddenAssets.find(token => token.assetGuid?.toLowerCase() === address?.toLowerCase())) {
            this.addAssetForm.controls[`assetGuid`].setErrors({ hiddenAsset: true });
            this.addAssetForm.controls[`assetGuid`].markAsDirty();
            return false;
        }
        return true;
    }

    async fetchTokenMetadata(address: string) {
        const addresses = [address];
        const selectedNetwork = this.addAssetForm.get('baseChainSymbol').value;
        const chainId = await this.moralisService.findChainIdAgainstNetwork(selectedNetwork);
        try {
            const response: any = await this.moralisService.getEVMWalletTokenMetadata(addresses, chainId);
            Logger.info('fetchTokenMetadata', response?.data);
            if (response?.data[0]?.name === '') {
                this.addAssetForm.controls[`assetGuid`].setErrors({ invalid: true });
                this.addAssetForm.controls[`assetGuid`].markAsDirty();
                return;
            }
            const baseChainSymbol: string = this.addAssetForm.get('baseChainSymbol').value;
            const balance: string = await this.getTokenBalance(baseChainSymbol);
            const logo: string = await this.moralisService.getRandomLogoForHiddenAsset();
            const tokenDetails = response?.data?.map(x => {
                const data: TokenDetailsFromMoralis = { precision: '', symbol: '', assetGuid: '', logo: '', balance: '', chainId: '', name: '' };
                data.precision = x.decimals;
                data.symbol = x.symbol;
                data.assetGuid = x.address;
                data.balance = this.smallToCrypto.transform(balance, { type: baseChainSymbol, precision: x.decimals });
                data.logo = x.logo ? x.logo : logo;
                data.chainId = chainId.apiId;
                data.name = x.name;
                return data;
            });
            return tokenDetails;
        } catch (e) {
            Logger.error('fetchTokenMetadata', inspect(e));
        }
    }

    async getTokenBalance(baseChainSymbol: string) {
        try {
            const walletAddressOfSelectedNetwork = this.getWalletAddress(baseChainSymbol);
            const assetGuid = this.addAssetForm.get('assetGuid').value;
            const chainId = await this.moralisService.findChainIdAgainstNetwork(baseChainSymbol);
            const res: any = await this.moralisService.getAllERC20TokensOwnedByAnAddress(
                walletAddressOfSelectedNetwork,
                chainId,
                [assetGuid]
            );
            Logger.info('getTokenBalance', res, walletAddressOfSelectedNetwork, assetGuid, chainId);
            return res?.data[0]?.balance;
        } catch (e) {
            Logger.error('getTokenBalance', inspect(e));
        }
    }

    async setTokenDetails(data: TokenDetailsFromMoralis) {
        const coingeckoId = await this.coingeckoService.getCoinId(data.symbol.toLowerCase());
        this.tokenMetadataFromMoralis = data;
        this.addAssetForm.patchValue({
            precision: +data.precision,
            symbol: data.symbol,
            logo: data.logo,
            balance: data.balance,
            coingeckoId,
            chainId: +data.chainId,
            name: data.name,
        });
        Logger.info('setTokenDetails', this.addAssetForm.value);

    }



    validateAddress(address: string) {
        // check validity of address
        Logger.info('validateAddress', address);
        try {
            ethers.utils.getAddress(address);
            return true;
        } catch (e) {
            Logger.error('invalid address', e);
            this.addAssetForm.controls[`assetGuid`].setErrors({ invalid: true });
            this.addAssetForm.controls[`assetGuid`].markAsDirty();
        }
    }

    async importOrHideAsset(isImport: boolean) {
        this.moralisService.setERC20tokenData(this.addAssetForm.value, this.tokensForImportERC20token);
        const sideMenuHidden = await this.store.select(getSideMenuStatus).pipe(take(1)).toPromise();
        let cssClass = '';
        cssClass += (this.isDesktopMode) ? 'purchase-gas-modal-desktop' : 'purchase-gas-modal';
        cssClass += (!sideMenuHidden && this.isDesktopMode) ? ' with-side-menu' : '';
        const modal = await this.modalController.create({
            component: ImportOrHideAssetModalComponent,
            cssClass,
            componentProps: {
                isImport,
                importFromVault: false
            },
            initialBreakpoint: (this.isDesktopMode) ? undefined : 0.5,
            breakpoints: (this.isDesktopMode) ? undefined : [0, 0.5, 0.6, 1],
            id: 'importOrHideAssetModal'
        });
        await modal.present();
        await modal.onDidDismiss().then();
    }

    private getWalletAddress(selectedNetwork: string) {
        const walletAddressOfSelectedNetwork = this.tokensForImportERC20token.find(token =>
            token.baseChainSymbol === selectedNetwork).address;
        Logger.info('walletAddressOfSelectedNetwork', walletAddressOfSelectedNetwork);
        this.addAssetForm.get('walletAddressOfSelectedNetwork').setValue(walletAddressOfSelectedNetwork);
        return walletAddressOfSelectedNetwork;
    }


}
